<!-- src/views/users/UsersTablePage.vue -->
<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/api'
import Swal from 'sweetalert2'
import useServerTable from '@/views/composables/useServerTable'
import UserQuickPeek from '@/views/Socios/components/UserQuickPeek.vue'
import { encodeId } from '@/utils/obfuscate'
import { hasPermission } from '@/composables/useAuth'

const canRegistrarAsistencia = computed(() => hasPermission('asistencias.registrar'))
const registrandoId = ref(null)

async function registrarAsistencia(u) {
  const r = await Swal.fire({
    title: '¿Registrar asistencia?',
    text: `Se registrará la asistencia de "${u.nombre}" (solo si tiene membresía vigente).`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, registrar',
    cancelButtonText: 'Cancelar'
  })
  if (!r.isConfirmed) return
  registrandoId.value = u.id
  try {
    await api.post('/accesos', { usuario_id: u.id })
    Swal.fire('Registrada', 'Asistencia registrada correctamente', 'success')
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.error('Error en registrarAsistencia:', err)
    Swal.fire('No registrada', err?.response?.data?.message || err.message || 'No se pudo registrar la asistencia', 'warning')
  } finally {
    registrandoId.value = null
  }
}

const peekOpen = ref(false)
const peekUserId = ref(null)
// estado de la tabla (sin modales)
const rows = ref([])
const total = ref(0)
const loading = ref(false)
const avatarError = ref(false)
function onAvatarError() {
  avatarError.value = true
}
function openPeek(u) {
  peekUserId.value = u.id
  peekOpen.value = true
}
const iniciales = computed(() => {
  const n = rows.value?.nombre?.trim() || ''
  if (!n) return '—'
  const parts = n.split(/\s+/).slice(0, 2) // máx 2 palabras
  return parts.map(p => p[0]?.toUpperCase() || '').join('') || '—'
})

// columnas disponibles para ordenar (backend debe soportarlas)
const SORTABLE = ['id', 'nombre', 'email', 'identificacion', 'rol', 'estado']

const router = useRouter()
const route = useRoute()



// filtros/tabla sincronizados al querystring
const { q, page, perPage, sortBy, sortDir } = useServerTable({
  q: route.query.q ?? '',
  page: Number(route.query.page ?? 1),
  perPage: Number(route.query.perPage ?? 10),
  sortBy: route.query.sortBy ?? 'id',
  sortDir: route.query.sortDir ?? 'asc'
})

// filtros adicionales
const rol = ref(route.query.rol ?? '')        // ADMINISTRADOR | EMPLEADO | SOCIO
const estado = ref(route.query.estado ?? '')  // 1 | 0 (activos/inactivos)

const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / perPage.value))
)

async function fetchUsers() {
  loading.value = true
  try {
    const { data } = await api.get('/users', {
      params: {
        page: page.value,
        limit: perPage.value,
        q: q.value || undefined,
        rol: rol.value || undefined,        // si tu backend usa rol_id ajusta aquí
        estado: estado.value || undefined,  // si usa booleano, ajusta a true/false
        sortBy: SORTABLE.includes(sortBy.value) ? sortBy.value : 'id',
        sortDir: sortDir.value
      }
    })
    rows.value = data.usuarios || data.rows || []
    total.value = data.total || data.total_items || 0
  } finally {
    loading.value = false
  }
}

// sincroniza con URL y hace fetch
watch([q, page, perPage, sortBy, sortDir, rol, estado], () => {
  router.replace({
    query: {
      q: q.value || undefined,
      page: page.value !== 1 ? page.value : undefined,
      perPage: perPage.value !== 10 ? perPage.value : undefined,
      sortBy: sortBy.value !== 'id' ? sortBy.value : undefined,
      sortDir: sortDir.value !== 'asc' ? sortDir.value : undefined,
      rol: rol.value || undefined,
      estado: estado.value || undefined
    }
  })
  fetchUsers()
}, { immediate: true })

function toggleSort(col) {
  if (!SORTABLE.includes(col)) return
  if (sortBy.value === col) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = col
    sortDir.value = 'asc'
  }
}

function goCreate() {
  router.push({ name: 'UsuarioCrear' })
}
function goEdit(row) {
  router.push({ name: 'UsuarioEditar', params: { id: encodeId(row.id) } })
}
function goDetail(row) {
  router.push({ name: 'SocioDetalle', params: { id: encodeId(row.id) } })
}

function prevPage() {
  if (page.value > 1) page.value -= 1
}
function nextPage() {
  if (page.value < totalPages.value) page.value += 1
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card p-4">
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 class="mb-0">Usuarios</h5>
        <div class="d-flex gap-2">
          <button class="btn btn-primary btn-sm" @click="goCreate">
            + Nuevo usuario
          </button>
        </div>
      </div>

      <!-- filtros estilo DataTables -->
      <div class="row g-2 mb-3">
        <div class="col-12 col-md-6">
          <input v-model="q" type="search" class="form-control form-control-sm"
            placeholder="Buscar por nombre, email, cédula…" />
        </div>
        <div class="col-6 col-md-3">
          <select v-model="rol" class="form-select form-select-sm">
            <option value="">— Rol (todos) —</option>
            <option value="ADMINISTRADOR">ADMINISTRADOR</option>
            <option value="EMPLEADO">EMPLEADO</option>
            <option value="SOCIO">SOCIO</option>
          </select>
        </div>
        <div class="col-6 col-md-3">
          <select v-model="estado" class="form-select form-select-sm">
            <option value="">— Estado (todos) —</option>
            <option value="1">Activo</option>
            <option value="0">Inactivo</option>
          </select>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-sm align-items-center custom-table text-sm">
          <thead>

            <tr>
              <th></th>
              <th @click="toggleSort('id')" style="cursor:pointer;">
                ID
                <small v-if="sortBy === 'id'">({{ sortDir }})</small>
              </th>
              <th @click="toggleSort('nombre')" style="cursor:pointer;">
                Nombre
                <small v-if="sortBy === 'nombre'">({{ sortDir }})</small>
              </th>
              <th @click="toggleSort('email')" style="cursor:pointer;">
                Email
                <small v-if="sortBy === 'email'">({{ sortDir }})</small>
              </th>
              <th @click="toggleSort('identificacion')" style="cursor:pointer;">
                Identificación
                <small v-if="sortBy === 'identificacion'">({{ sortDir }})</small>
              </th>
              <th>Rol</th>
              <th @click="toggleSort('estado')" style="cursor:pointer;">
                Estado
                <small v-if="sortBy === 'estado'">({{ sortDir }})</small>
              </th>
              <th class="text-end">Acciones</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center py-4">Cargando…</td>
            </tr>
            <tr v-for="u in rows" :key="u.id">
              <td>


                <div class="avatar-wrapper">
                  <img v-if="u?.avatar_url && !avatarError" :src="u.avatar_url" class="avatar-img" alt="Avatar"
                    loading="lazy" decoding="async" @error="onAvatarError">
                  <div v-else class="avatar-fallback">{{ iniciales }}</div>
                </div>
              </td>
              <td>{{ u.id }}</td>
              <td>
                <button class="btn btn-link p-0 text-decoration-none" @click.stop="openPeek(u)">
                  {{ u.nombre }}
                </button>
              </td>
              <td>{{ u.email }}</td>
              <td>{{ u.identificacion }}</td>
              <td class="text-nowrap">
                <span class="badge" :class="{
                  'bg-success': u.rol?.nombre === 'ADMINISTRADOR',
                  'bg-secondary': u.rol?.nombre === 'EMPLEADO',
                  'bg-primary': u.rol?.nombre === 'SOCIO',
                }">
                  {{ u.rol?.nombre || '—' }}
                </span>
              </td>
              <td>
                <span :class="u.estado ? 'badge bg-success' : 'badge bg-secondary'">
                  {{ u.estado ? 'Activo' : 'Inactivo' }}
                </span>
              </td>
              <td class="text-end text-nowrap">
                <button v-if="canRegistrarAsistencia && u.rol?.nombre === 'SOCIO'" class="btn btn-outline-success btn-xs me-1" title="Registrar asistencia" :disabled="registrandoId === u.id" @click="registrarAsistencia(u)">
                  <i class="ni ni-check-bold text-success text-xs"></i>
                </button>
                <button class="btn btn-outline-info btn-xs me-1" title="Detalle" @click="goDetail(u)">
                  <i class="ni ni-collection text-info text-xs"></i>
                </button>
                <button class="btn btn-outline-secondary btn-xs" title="Editar" @click="goEdit(u)">
                  <i class="ni ni-settings text-secondary text-xs"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!loading && rows.length === 0">
              <td colspan="7" class="text-center py-4">Sin resultados</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- paginación -->
      <div class="d-flex justify-content-between align-items-center mt-3 px-2 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-2">
          <small class="text-muted">Página {{ page }} de {{ totalPages }}</small>
          <select v-model.number="perPage" class="form-select form-select-sm" style="width:90px">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary btn-sm" :disabled="page === 1" @click="prevPage">Anterior</button>
          <button class="btn btn-outline-secondary btn-sm" :disabled="page === totalPages"
            @click="nextPage">Siguiente</button>
        </div>
      </div>
    </div>
  </div>
  <UserQuickPeek :open="peekOpen" :user-id="peekUserId" @close="peekOpen = false" />
</template>

<style scoped>
.custom-table th,
.custom-table td {
  padding: 0.25rem 0.4rem !important;
  font-size: 0.85rem !important;
  line-height: 1.2;
  white-space: nowrap;
}

.custom-table thead {
  background-color: #f2f2f2;
  color: #333;
  font-size: 0.8rem;
  text-transform: uppercase;
}

.custom-table tbody tr:hover {
  background-color: #f8f9fa;
}

.avatar-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f2f5;
  display: grid;
  place-items: center
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover
}
</style>
